import { getFunctionUrl } from '../config/v2FunctionUrls';
import { SOURCE_CODES } from '../config/marketingSources';

const STORAGE_KEY = 'aiprep_attribution_source';
const TRACKED_KEY = 'aiprep_page_tracked';

/**
 * Attribution service for tracking marketing sources and conversions.
 *
 * Flow:
 * 1. User visits with ?ref=xx → source stored in localStorage
 * 2. Anonymous page_view event sent to Firestore
 * 3. On signup → source sent to backend and stored on user profile
 * 4. On payment → payment event logged with source (in Stripe webhook)
 */
export const attributionService = {
  /**
   * Track page view and persist source for conversion tracking.
   * Called on app load.
   */
  trackPageView: async () => {
    const params = new URLSearchParams(window.location.search);
    const refCode = params.get('ref');

    if (refCode) {
      const source = SOURCE_CODES[refCode];
      if (source) {
        // Persist source in localStorage (survives until signup)
        // Only set if not already set (first touch attribution)
        if (!localStorage.getItem(STORAGE_KEY)) {
          localStorage.setItem(STORAGE_KEY, source);
        }
      }

      // Clean ref param from URL so users don't share tracking links
      const url = new URL(window.location.href);
      url.searchParams.delete('ref');
      window.history.replaceState({}, '', url.toString());
    }

    // Track anonymous page view (once per session)
    const source = localStorage.getItem(STORAGE_KEY);
    if (!source) return;
    if (sessionStorage.getItem(TRACKED_KEY)) return;
    sessionStorage.setItem(TRACKED_KEY, 'true');

    try {
      // trackAttribution only handles anonymous page views (eventType not needed)
      await fetch(getFunctionUrl('trackAttribution'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          source,
          referrer: document.referrer || undefined,
        }),
        keepalive: true,
      });
    } catch {
      // Silent fail - non-critical
    }
  },

  /**
   * Get stored attribution source for passing to backend on signup.
   * Returns null if no source tracked.
   */
  getStoredSource: (): string | null => {
    return localStorage.getItem(STORAGE_KEY);
  },

  /**
   * Clear stored source after successful signup attribution.
   * Called after backend confirms source was stored on user profile.
   */
  clearStoredSource: () => {
    localStorage.removeItem(STORAGE_KEY);
  },
};
